import * as i0 from '@angular/core';
import { InjectionToken, inject, ElementRef, Directive, Input, EventEmitter, Output, HostListener, signal, computed, Component } from '@angular/core';
import autoAnimate from '@formkit/auto-animate';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import * as i1 from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
const isPlugin = config => typeof config === 'function';
const GLOBAL_AUTO_ANIMATE_OPTIONS = new InjectionToken('Global AutoAnimate Options or Plugin', {
  factory: () => ({})
});
let LepiAutoAnimateDirective = /*#__PURE__*/(() => {
  class LepiAutoAnimateDirective {
    constructor() {
      this.el = inject(ElementRef);
      this.globalOptions = inject(GLOBAL_AUTO_ANIMATE_OPTIONS);
      this._options = {};
    }
    set explicitOptions(_options) {
      if (typeof _options === 'string') {
        // Default case, when no options or plugin is passed
        this._options = this.globalOptions;
      } else {
        // When either some options or a plugin is passed
        if (isPlugin(this.globalOptions) || isPlugin(_options)) {
          // A plugin must replace any previously set options or plugin.
          // A plugin must be replaced by options or another plugin.
          this._options = _options;
        } else {
          // When plugins are not involved
          this._options = {
            ...this.globalOptions,
            ..._options
          };
        }
      }
    }
    get options() {
      return this._options;
    }
    ngAfterViewInit() {
      autoAnimate(this.el.nativeElement, this.options);
    }
    static {
      this.ɵfac = function LepiAutoAnimateDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LepiAutoAnimateDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LepiAutoAnimateDirective,
        selectors: [["", "auto-animate", ""]],
        inputs: {
          explicitOptions: [0, "auto-animate", "explicitOptions"]
        },
        standalone: true
      });
    }
  }
  return LepiAutoAnimateDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LepiClickOutsideDirective = /*#__PURE__*/(() => {
  class LepiClickOutsideDirective {
    constructor(elementRef) {
      this.elementRef = elementRef;
      this.outside = new EventEmitter();
    }
    onGlobalClick(event) {
      if (!this.elementRef.nativeElement.contains(event.target)) {
        this.outside.emit(event);
      }
    }
    static {
      this.ɵfac = function LepiClickOutsideDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LepiClickOutsideDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LepiClickOutsideDirective,
        selectors: [["", "lepiClickOutside", ""]],
        hostBindings: function LepiClickOutsideDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("mousedown", function LepiClickOutsideDirective_mousedown_HostBindingHandler($event) {
              return ctx.onGlobalClick($event);
            }, false, i0.ɵɵresolveDocument);
          }
        },
        outputs: {
          outside: "outside"
        },
        standalone: true
      });
    }
  }
  return LepiClickOutsideDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LepiValueAccessorDirective = /*#__PURE__*/(() => {
  class LepiValueAccessorDirective {
    constructor() {
      this._value = signal(null);
      this._disabled = signal(false);
      this.value = computed(() => this._value());
      this.disabled = computed(() => this._disabled());
    }
    valueChange(v) {
      this.onChange(v);
    }
    touchedChange(v) {
      this.onTouched(v);
    }
    writeValue(obj) {
      this._value.set(obj);
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
      this._disabled.set(isDisabled);
    }
    static {
      this.ɵfac = function LepiValueAccessorDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LepiValueAccessorDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LepiValueAccessorDirective,
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: NG_VALUE_ACCESSOR,
          useExisting: LepiValueAccessorDirective,
          multi: true
        }])]
      });
    }
  }
  return LepiValueAccessorDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LepiTooltipDirective = /*#__PURE__*/(() => {
  class LepiTooltipDirective {
    constructor(overlay, elementRef, overlayPositionBuilder) {
      this.overlay = overlay;
      this.elementRef = elementRef;
      this.overlayPositionBuilder = overlayPositionBuilder;
      this.tooltipText = '';
      this.tooltipXPosition = 'middle';
      this.tooltipYPosition = 'top';
      this.overlayRef = null;
    }
    ngOnInit() {
      const positionStrategy = this.overlayPositionBuilder.flexibleConnectedTo(this.elementRef).withPositions([this.getPosition()]);
      this.overlayRef = this.overlay.create({
        positionStrategy
      });
    }
    show() {
      if (!this.overlayRef) return;
      const tooltipPortal = new ComponentPortal(LepiTootlip);
      const tooltipRef = this.overlayRef.attach(tooltipPortal);
      tooltipRef.instance.text = this.tooltipText;
    }
    hide() {
      this.overlayRef?.detach();
    }
    getPosition() {
      if (this.tooltipXPosition === 'left') {
        if (this.tooltipYPosition === 'bottom') {
          return {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          };
        }
        if (this.tooltipYPosition === 'center') {
          return {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center'
          };
        }
        return {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        };
      }
      if (this.tooltipXPosition === 'right') {
        if (this.tooltipYPosition === 'bottom') {
          return {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          };
        }
        if (this.tooltipYPosition === 'center') {
          return {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center'
          };
        }
        return {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        };
      }
      if (this.tooltipXPosition === 'middle') {
        if (this.tooltipYPosition === 'bottom') {
          return {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top'
          };
        }
        if (this.tooltipYPosition === 'center') {
          return {
            originX: 'center',
            originY: 'center',
            overlayX: 'center',
            overlayY: 'center'
          };
        }
        return {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom'
        };
      }
      return {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top'
      };
    }
    static {
      this.ɵfac = function LepiTooltipDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LepiTooltipDirective)(i0.ɵɵdirectiveInject(i1.Overlay), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.OverlayPositionBuilder));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LepiTooltipDirective,
        selectors: [["", "lepiTooltip", ""]],
        hostBindings: function LepiTooltipDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("mouseenter", function LepiTooltipDirective_mouseenter_HostBindingHandler() {
              return ctx.show();
            })("mouseout", function LepiTooltipDirective_mouseout_HostBindingHandler() {
              return ctx.hide();
            });
          }
        },
        inputs: {
          tooltipText: "tooltipText",
          tooltipXPosition: "tooltipXPosition",
          tooltipYPosition: "tooltipYPosition"
        },
        standalone: true
      });
    }
  }
  return LepiTooltipDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LepiTootlip = /*#__PURE__*/(() => {
  class LepiTootlip {
    constructor() {
      this.text = '';
    }
    static {
      this.ɵfac = function LepiTootlip_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LepiTootlip)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: LepiTootlip,
        selectors: [["lepi-tooltip"]],
        inputs: {
          text: "text"
        },
        decls: 3,
        vars: 2,
        consts: [[1, "p-1", "m-0.5", "rounded-md", "bg-neutral-800", "font-bold", "text-white", "text-xs"]],
        template: function LepiTootlip_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "span", 0);
            i0.ɵɵtext(1);
            i0.ɵɵelement(2, "span");
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵproperty("@fadeInOut", undefined);
            i0.ɵɵadvance();
            i0.ɵɵtextInterpolate(ctx.text);
          }
        },
        encapsulation: 2,
        data: {
          animation: [trigger('fadeInOut', [transition(':enter', [style({
            opacity: 0
          }), animate('100ms', style({
            opacity: 1
          }))]), transition(':leave', [animate('100ms', style({
            opacity: 0
          }))])])]
        }
      });
    }
  }
  return LepiTootlip;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { GLOBAL_AUTO_ANIMATE_OPTIONS, LepiAutoAnimateDirective, LepiClickOutsideDirective, LepiTooltipDirective, LepiTootlip, LepiValueAccessorDirective };
